//@ts-check

/**
 * @fileoverview StaticPayLink - Static Payment Link Page Component
 *
 * This component handles static payment links where no invoice or customer
 * is pre-associated. Users can make one-time payments by entering their
 * own payment details and amount.
 *
 * @description
 * StaticPayLink fetches location data based on the locationId from URL params,
 * then renders the PaymentForm component for payment collection.
 *
 * Key differences from InvoicePayLink:
 * - No invoice or customer pre-loaded
 * - User enters their own amount and contact details
 * - Uses runStaticPayLinkPayment service instead of runInvoicePayLinkPayment
 * - URL pattern: /selfPayment/:locationId
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, CircularProgress, Backdrop } from '@mui/material';

import payLinkService from '../../services/payLinkService';
import PaymentForm from './components/PaymentForm';
import InvoicePayLinkErrorBoundary from './components/InvoicePayLinkErrorBoundary';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import PaymentConfirmationV2 from '../../components/PaymentConfirmationV2/PaymentConfirmationV2';
import commonStrings from '../../constants/commonStrings';
import locationsService from '../../services/locationsService';

const messageConstants = require('../../constants/message');

/** @constant Cleanup delay for blob URLs in milliseconds */
const BLOB_CLEANUP_DELAY = 1000;

/** @typedef {import('../../../../types/customers').CustomerViewModel} CustomerInput */
/** @typedef {import('../../../../types/StaticPayLink').StaticPaymentPayload} StaticPaymentPayload */
/** @typedef {import('../../../../types/PaymentConfirmationV2').PaymentResponseData} PaymentResponseData */

/**
 * @component StaticPayLink
 * @description Static payment link page for one-time payments without pre-loaded invoice.
 *
 * Features:
 * - Location-based payment collection
 * - User-entered amount and contact details
 * - Card and ACH payment support
 * - Fee calculation
 * - reCAPTCHA validation
 *
 * @returns {JSX.Element} Static payment link page
 */
const StaticPayLink = () => {
  const { locationId } = useParams();

  // Loading and UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState(/** @type {string[]} */ ([]));
  const [warnings, setWarnings] = useState(/** @type {string[]} */ ([]));
  const [fieldErrors, setFieldErrors] = useState({});

  // Data state (fetched from API)
  const [location, setLocation] = useState(/** @type {LocationViewModel | null} */ (null));
  const [feeSettings, setFeeSettings] = useState(/** @type {DatabaseFeeSettings | null} */ (null));
  const [termsConditions, setTermsConditions] = useState(/** @type {TermsConditions | null} */ (null));

  // User input state
  const [amount, setAmount] = useState('');
  const [customerInput, setCustomerInput] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [invoiceNumber, setInvoiceNumber] = useState('');

  // Payment success state
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(/** @type {PaymentResponseData | null} */ (null));
  const [calculationResult, setCalculationResult] = useState(null);
  const [lastPaymentData, setLastPaymentData] = useState(null);

  // TSEP state (for Multipass gateway)
  const [tsepFieldSuccess, setTsepFieldSuccess] = useState(false);
  const [tsepFieldError, setTsepFieldError] = useState(/** @type {string | null} */ (null));

  /**
   * @description Deduplicated error handling based on HTTP status code
   * Renders warnings for 410/409 status, errors for others.
   *
   * @param {number} status - HTTP status code
   * @param {string} message - Error message
   */
  const handleErrorsByStatus = useCallback((status, message) => {
    /** @type {string[]} */
    const warningsList = [];
    /** @type {string[]} */
    const errorsList = [];

    if (message && (status === 410 || status === 409)) {
      warningsList.push(message);
    } else if (message) {
      errorsList.push(message);
    } else {
      errorsList.push(`${messageConstants.UNKNOWN_ERROR} ${messageConstants.CONTACT_SUPPORT}`);
    }

    if (warningsList.length > 0) {
      setWarnings((prev) => [...prev, ...warningsList]);
    }
    if (errorsList.length > 0) {
      setErrors((prev) => [...prev, ...errorsList]);
    }
  }, []);

  /**
   * @description Fetch location data via ViewModel (secure)
   * @param {string} locId - Location ID
   */
  const fetchLocationData = async (locId) => {
    if (!locId) return;

    try {
      const response = await payLinkService.getLocationViewModelById(locId);
      const locationData = response.data?.location;

      if (locationData) {
        setLocation(locationData);
        localStorage.setItem('locationPaydetails', JSON.stringify(locationData));

        // Initialize Paytrace if needed
        if (locationData.gateway === commonStrings.paytrace && window.paytrace) {
          window.paytrace.setKeyAjax(payLinkService.getPublicKeyUri(locId));
        }
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching location:', err);
      setErrors((prev) => [...prev, `Unable to load location data.`]);
    }
  };

  /**
   * @description Fetch TSEP form data for Multipass gateway
   * @param {string} locId - Location ID
   * @param {LocationViewModel} locationData - Current location data
   */
  const fetchTsepFormData = async (locId, locationData) => {
    if (!locId || !locationData) return;

    try {
      const tsepResponse = await payLinkService.getTsepFormDataByLocationId(locId);
      if (tsepResponse.data?.tsepFormData) {
        const updatedLocation = {
          ...locationData,
          multipassCredentials: tsepResponse.data.tsepFormData,
        };
        setLocation(/** @type {LocationViewModelWithTsepFormData} */ (updatedLocation));
        localStorage.setItem('locationPaydetails', JSON.stringify(updatedLocation));
      }
    } catch (/** @type {AxiosError | any} */ error) {
      console.error('Error fetching TSEP form data:', error);
    }
  };

  /**
   * @description Fetch user settings for fee calculation
   * @param {string} locId - Location ID
   */
  const fetchUserSettings = async (locId) => {
    if (!locId) return;

    try {
      const response = await payLinkService.getUserSettingsByLocationId(locId);
      if (response.data?.userSettings) {
        setFeeSettings(response.data.userSettings);
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching user settings:', err);
    }
  };

  /**
   * @description Fetch terms and conditions
   * @param {string} locId - Location ID
   */
  const fetchTermsConditions = async (locId) => {
    if (!locId) return;

    try {
      const response = await payLinkService.getTermsConditionsByLocationId(locId);
      if (response.data?.termsConditions) {
        setTermsConditions(response.data.termsConditions);
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching terms:', err);
      setErrors((prev) => [...prev, `Unable to load terms and conditions.`]);
    }
  };

  /**
   * @description Initial data load when locationId is available
   */
  useEffect(() => {
    if (!locationId || isPaymentSuccess) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchLocationData(locationId);
        await Promise.all([fetchUserSettings(locationId), fetchTermsConditions(locationId)]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [locationId, isPaymentSuccess]);

  /**
   * @description Fetch TSEP credentials when location gateway is Multipass
   */
  useEffect(() => {
    if (!location || location.gateway !== commonStrings.multipass || isPaymentSuccess) return;
    if (location._id) {
      fetchTsepFormData(location._id, location);
    }
  }, [location?.gateway, location?._id, isPaymentSuccess]);

  /**
   * @description Get reCAPTCHA token for payment submission
   * @returns {Promise<string|null>} reCAPTCHA token or null if failed
   */
  const getRecaptchaToken = useCallback(async () => {
    if (!window.grecaptcha) {
      setErrors([`Unable to process payment, CAPTCHA could not load.`]);
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_KEY || '', { action: 'submit' });
      if (!token || typeof token !== 'string') {
        setErrors([`Unable to process payment, CAPTCHA could not load.`]);
        return null;
      }
      return token;
    } catch (/** @type {Error | any} */ err) {
      console.error('reCAPTCHA error:', err);
      setErrors([`Unable to process payment, CAPTCHA could not load.`]);
      return null;
    }
  }, []);

  /**
   * @description Validate customer input fields
   * @returns {{ isValid: boolean, errors: string[] }}
   */
  const validateCustomerInput = useCallback(() => {
    const validationErrors = [];

    if (!customerInput.firstName.trim()) {
      validationErrors.push('Please enter a first name.');
    }
    if (!customerInput.email.trim()) {
      validationErrors.push('Please enter an email address.');
    }
    if (!amount || Number.isNaN(Number.parseFloat(amount)) || Number.parseFloat(amount) <= 0) {
      validationErrors.push('Please enter a valid amount.');
    }
    if (!invoiceNumber.trim()) {
      validationErrors.push('Please enter an invoice/reference number.');
    }

    return { isValid: validationErrors.length === 0, errors: validationErrors };
  }, [customerInput.firstName, customerInput.email, amount, invoiceNumber]);

  /**
   * @description Handle payment form submission
   * @param {Object} paymentData - Payment data from PaymentForm
   */
  const handleSubmit = useCallback(
    async (paymentData) => {
      setErrors([]);
      setIsSubmitting(true);

      try {
        // Validate customer inputs
        const { isValid, errors: validationErrors } = validateCustomerInput();
        if (!isValid) {
          setErrors(validationErrors);
          setIsSubmitting(false);
          return;
        }

        // Get reCAPTCHA token
        const recaptchaToken = await getRecaptchaToken();
        if (!recaptchaToken) {
          setErrors([`reCAPTCHA validation failed.`]);
          setIsSubmitting(false);
          return;
        }

        if (!amount || Number.isNaN(Number.parseFloat(amount)) || Number.parseFloat(amount) <= 0) {
          setErrors([`A valid amount is required.`]);
          setIsSubmitting(false);
          return;
        }

        // Build payload for static payment
        /** @type {StaticPaymentPayload} */
        const payload = {
          paymentMethod: paymentData.paymentMethod,
          customer: {
            firstName: customerInput.firstName,
            lastName: customerInput.lastName,
            email: customerInput.email,
            phone: customerInput.phone,
          },
          baseAmount: amount.toString(),
          orderId: invoiceNumber,
          billingAddress: paymentData.billingAddress,
          shippingAddress: paymentData.shippingAddress,
          recaptchaToken,
        };

        // Submit payment
        if (!location?._id) {
          throw new Error('Location ID is required for payment processing');
        }
        const response = await payLinkService.runStaticPayLinkPayment(location._id, payload);

        // Store data for confirmation display
        setCalculationResult(paymentData.calculationResult);
        setLastPaymentData(paymentData);

        // Handle success
        setIsPaymentSuccess(true);

        setPaymentResponse({
          message: response?.data?.message || 'Payment processed successfully',
          transactionId: response?.data?.transactionId || '',
        });
      } catch (/** @type {AxiosError | any} */ err) {
        if (err?.response?.status && err?.response?.data?.message) {
          handleErrorsByStatus(err?.response?.status, err?.response?.data?.message);
        } else {
          setErrors([`Payment failed.`]);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [location, customerInput, amount, invoiceNumber, validateCustomerInput, getRecaptchaToken, handleErrorsByStatus]
  );

  /**
   * @description Handle policy PDF download
   * @param {string} policyType - Type of policy to view
   */
  const handleViewPolicy = useCallback(
    async (policyType) => {
      if (
        termsConditions &&
        typeof policyType === 'string' &&
        termsConditions[/** @type {keyof TermsConditions} */ (policyType)]
      ) {
        try {
          if (!location?._id) {
            setErrors((prev) => [...prev, 'Location data is missing. Cannot load policy.']);
            return;
          }
          const response = await payLinkService.getPolicyFile(location._id, policyType);
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_CLEANUP_DELAY);
        } catch (error) {
          console.error('Policy viewing error:', error);
          setErrors((prev) => [...prev, 'Failed to load the policy document. Please try again.']);
        }
      } else {
        setErrors((prev) => [...prev, 'The requested policy document is not available.']);
      }
    },
    [location?._id, termsConditions]
  );

  /**
   * @description Remove alert message at index
   * @param {number} index - Index of alert to remove
   * @param {React.Dispatch<React.SetStateAction<string[]>>} setter - State setter function for the alert array
   */
  const removeAlert = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // Show loading spinner while fetching initial data
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 0, px: 0 }}>
        <Backdrop open={true} sx={{ zIndex: 9999, color: '#fff' }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 0, px: 0 }}>
      {/* Loading Backdrop */}
      <Backdrop open={isSubmitting} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Warning Messages */}
      {warnings.length > 0 && (
        <FlexAlerts
          messages={warnings.map((message, index) => ({
            severity: AlertSeverityTypes.warning,
            message,
            action: {
              text: 'Close',
              onClick: () => removeAlert(index, setWarnings),
              'data-cy': `StaticPayLink-warning-alert-close-${index}`,
            },
          }))}
        />
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <FlexAlerts
          messages={errors.map((message, index) => ({
            severity: AlertSeverityTypes.error,
            message,
            action: {
              text: 'Close',
              onClick: () => removeAlert(index, setErrors),
              'data-cy': `StaticPayLink-error-alert-close-${index}`,
            },
          }))}
        />
      )}

      {/* Payment Success */}
      {isPaymentSuccess && paymentResponse && lastPaymentData && (
        <PaymentConfirmationV2
          invoiceNumber={invoiceNumber}
          paymentResponse={paymentResponse}
          paymentData={lastPaymentData}
          customerName={`${customerInput.firstName} ${customerInput.lastName}`.trim()}
          calculationResult={calculationResult}
          locationName={location?.name}
          locationImageUrl={locationsService.getLocationImageByIdUrl(location?._id || '')}
          feeSettings={feeSettings}
        />
      )}

      {/* Payment Form */}
      {!isPaymentSuccess && location && (
        <PaymentForm
          location={location}
          feeSettings={feeSettings}
          termsConditions={termsConditions}
          baseAmount={amount}
          onSubmit={handleSubmit}
          onViewPolicy={handleViewPolicy}
          isLoading={isSubmitting}
          setErrors={setErrors}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          showInvoiceDetails={false}
          firstName={customerInput.firstName}
          setFirstNameField={(/** @type {string} */ value) =>
            setCustomerInput((prev) => ({ ...prev, firstName: value }))
          }
          lastName={customerInput.lastName}
          setLastNameField={(/** @type {string} */ value) => setCustomerInput((prev) => ({ ...prev, lastName: value }))}
          email={customerInput.email}
          setEmailField={(/** @type {string} */ value) => setCustomerInput((prev) => ({ ...prev, email: value }))}
          phone={customerInput.phone}
          setPhoneField={(/** @type {string} */ value) => setCustomerInput((prev) => ({ ...prev, phone: value }))}
          amount={amount}
          setAmountField={setAmount}
          invoiceNumber={invoiceNumber}
          setInvoiceNumberField={setInvoiceNumber}
          tsepFieldSuccess={tsepFieldSuccess}
          setTsepFieldSuccess={setTsepFieldSuccess}
          tsepFieldError={tsepFieldError}
          setTsepFieldError={setTsepFieldError}
        />
      )}
    </Container>
  );
};

/**
 * @component StaticPayLinkWithErrorBoundary
 * @description StaticPayLink wrapped with error boundary for graceful error handling
 * @returns {JSX.Element} Wrapped component
 */
const StaticPayLinkWithErrorBoundary = () => (
  <InvoicePayLinkErrorBoundary>
    <StaticPayLink />
  </InvoicePayLinkErrorBoundary>
);

export default StaticPayLinkWithErrorBoundary;

/** @typedef {import('axios').AxiosPromise} AxiosPromise */
/** @typedef {import('axios').AxiosResponse} AxiosResponse */
/** @typedef {import('axios').AxiosError} AxiosError */
/** @typedef {import('../../../../types/locations').LocationViewModel} LocationViewModel */
/** @typedef {import('../../../../types/locations').LocationViewModelWithTsepFormData} LocationViewModelWithTsepFormData */
/** @typedef {import('../../../../types/paymentDataBuilder').PaymentData} PaymentData */
/** @typedef {import('../../../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings */
/** @typedef {import('../../../../types/termsConditions').TermsConditions} TermsConditions */
